import axios from 'axios';

/**
 * Transport-level class of a failed request. Only HTTP semantics live here; what a given 4xx
 * means for a product workflow is decided by the caller.
 */
export type RequestErrorKind =
  | 'client_error' // 4xx: the server understood the request and refused it
  | 'server_error' // 5xx
  | 'timeout'
  | 'network' // no response at all: offline, DNS, CORS, connection reset
  | 'cancelled'
  | 'unknown'; // not a request failure, e.g. an exception thrown by our own code

export type RequestErrorDetails = {
  kind: RequestErrorKind;
  httpStatus?: number;
  /** axios error code such as `ERR_NETWORK` — a bounded enum, safe to send to telemetry. */
  errorCode?: string;
};

const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);

export const classifyRequestError = (error: unknown): RequestErrorDetails => {
  if (!axios.isAxiosError(error)) {
    return { kind: 'unknown' };
  }

  const httpStatus = error.response?.status;
  const errorCode = error.code;

  if (httpStatus !== undefined) {
    return { kind: httpStatus >= 500 ? 'server_error' : 'client_error', httpStatus, errorCode };
  }
  if (axios.isCancel(error)) {
    return { kind: 'cancelled', errorCode };
  }
  if (errorCode && TIMEOUT_CODES.has(errorCode)) {
    return { kind: 'timeout', errorCode };
  }
  return { kind: 'network', errorCode };
};
